// frontend/src/components/Requisitions/edit/hooks/useStep2Catalogs.js

import { useEffect, useRef, useState } from "react";

import { STEP2_SPECS } from "../constants";
import { fetchFirstOk } from "../api";

import {
  dedupeById,
  normalizeProductRow,
  normalizeUnitRow,
  normalizeDescriptionRow,
  getProductLabel,
  getUnitLabel,
  descCacheKey,
} from "../utils";

const isCatalogItemActive = (item) => {
  if (!item || typeof item !== "object") return false;
  if (!Object.prototype.hasOwnProperty.call(item, "active")) return true;

  const value = item.active;
  if (typeof value === "string") {
    return !["false", "0", "no", "off", "inactive"].includes(value.trim().toLowerCase());
  }
  return Boolean(value);
};

export function useStep2Catalogs({
  step,
  currentTenderId,
  manualItemsMode,
  items,
  effectiveFormProductId,
  showCatalogModal,
  catalogModalProduct,
}) {
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [descOptions, setDescOptions] = useState([]);
  const [descCache, setDescCache] = useState({});
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [catalogModalDescs, setCatalogModalDescs] = useState([]);

  const productsRequestSeq = useRef(0);
  const descriptionsRequestSeq = useRef(0);
  const catalogModalDescriptionsSeq = useRef(0);

  // Cargar productos + unidades al entrar a paso 2 o cambiar tender/modo manual
  useEffect(() => {
    if (step !== 2) return;

    let cancelled = false;
    const requestId = ++productsRequestSeq.current;

    async function loadCatalogs() {
      setLoadingCatalogs(true);

      try {
        const unitsPromise = fetchFirstOk(STEP2_SPECS.unitsUrls).then((rows) =>
          dedupeById((rows || []).filter(isCatalogItemActive).map(normalizeUnitRow)).sort((a, b) =>
            getUnitLabel(a).localeCompare(getUnitLabel(b), "es")
          )
        );

        let productsPromise;

        if (manualItemsMode) {
          productsPromise = fetchFirstOk(STEP2_SPECS.productsUrlsAll).then((rows) =>
            dedupeById((rows || []).filter(isCatalogItemActive).map(normalizeProductRow)).sort((a, b) =>
              getProductLabel(a).localeCompare(getProductLabel(b), "es")
            )
          );
        } else if (currentTenderId) {
          productsPromise = fetchFirstOk(
            STEP2_SPECS.productsUrlsByTender(currentTenderId)
          ).then((rows) =>
            dedupeById((rows || []).filter(isCatalogItemActive).map(normalizeProductRow)).sort((a, b) =>
              getProductLabel(a).localeCompare(getProductLabel(b), "es")
            )
          );
        } else {
          productsPromise = Promise.resolve([]);
        }

        const [nextProducts, nextUnits] = await Promise.all([
          productsPromise,
          unitsPromise,
        ]);

        if (cancelled || requestId !== productsRequestSeq.current) return;

        setProducts(nextProducts);
        setUnits(nextUnits);
      } catch (err) {
        console.error(err);

        if (!cancelled) {
          setProducts([]);
          setUnits([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingCatalogs(false);
        }
      }
    }

    loadCatalogs();

    return () => {
      cancelled = true;
    };
  }, [step, currentTenderId, manualItemsMode]);

  // Cargar descripciones del producto seleccionado en el form
  useEffect(() => {
    if (step !== 2) return;

    if (manualItemsMode) {
      setDescOptions([]);
      return;
    }

    const productId = effectiveFormProductId;

    if (!productId || !currentTenderId) {
      setDescOptions([]);
      return;
    }

    let cancelled = false;
    const requestId = ++descriptionsRequestSeq.current;

    async function loadDescriptions() {
      try {
        const rows = await fetchFirstOk(
          STEP2_SPECS.itemDescriptionsUrls(currentTenderId, productId)
        );

        const normalized = dedupeById(
          rows
            .map(normalizeDescriptionRow)
            .filter((row) => String(row.productId) === String(productId))
            .filter((row) => String(row.tenderId) === String(currentTenderId))
        ).sort((a, b) => String(a.text || "").localeCompare(String(b.text || ""), "es"));

        if (cancelled || requestId !== descriptionsRequestSeq.current) return;

        setDescOptions(normalized);
        setDescCache((prev) => ({
          ...prev,
          [descCacheKey(currentTenderId, productId)]: normalized,
        }));
      } catch {
        if (!cancelled && requestId === descriptionsRequestSeq.current) {
          setDescOptions([]);
        }
      }
    }

    loadDescriptions();

    return () => {
      cancelled = true;
    };
  }, [step, manualItemsMode, currentTenderId, effectiveFormProductId]);

  // Precargar cache de descripciones para productos ya existentes en items
  useEffect(() => {
    if (manualItemsMode) return;
    if (step !== 2) return;
    if (!currentTenderId) return;

    const productIds = Array.from(
      new Set((items || []).map((item) => String(item.product)).filter(Boolean))
    );

    const missingProductIds = productIds.filter(
      (productId) => !descCache?.[descCacheKey(currentTenderId, productId)]
    );

    if (missingProductIds.length === 0) return;

    let cancelled = false;

    async function prefetchDescriptions() {
      try {
        const results = await Promise.allSettled(
          missingProductIds.map((productId) =>
            fetchFirstOk(STEP2_SPECS.itemDescriptionsUrls(currentTenderId, productId))
          )
        );

        if (cancelled) return;

        const patch = {};

        results.forEach((result, index) => {
          const productId = missingProductIds[index];

          if (result.status === "fulfilled") {
            patch[descCacheKey(currentTenderId, productId)] = dedupeById(
              (result.value || [])
                .filter(isCatalogItemActive)
                .map(normalizeDescriptionRow)
                .filter((row) => String(row.productId) === String(productId))
                .filter((row) => String(row.tenderId) === String(currentTenderId))
            ).sort((a, b) => String(a.text || "").localeCompare(String(b.text || ""), "es"));
          }
        });

        if (Object.keys(patch).length > 0) {
          setDescCache((prev) => ({
            ...prev,
            ...patch,
          }));
        }
      } catch {
        // silencio intencional
      }
    }

    prefetchDescriptions();

    return () => {
      cancelled = true;
    };
  }, [step, items, descCache, manualItemsMode, currentTenderId]);

  // Cargar descripciones para el modal "Ver Catálogo"
  useEffect(() => {
    if (manualItemsMode) return;
    if (!showCatalogModal) return;

    if (!catalogModalProduct || !currentTenderId) {
      setCatalogModalDescs([]);
      return;
    }

    let cancelled = false;
    const requestId = ++catalogModalDescriptionsSeq.current;

    async function loadCatalogModalDescriptions() {
      try {
        const rows = await fetchFirstOk(
          STEP2_SPECS.itemDescriptionsUrls(currentTenderId, catalogModalProduct)
        );

        const normalized = dedupeById(
          rows
            .filter(isCatalogItemActive)
            .map(normalizeDescriptionRow)
            .filter((row) => String(row.productId) === String(catalogModalProduct))
            .filter((row) => String(row.tenderId) === String(currentTenderId))
        ).sort((a, b) => String(a.text || "").localeCompare(String(b.text || ""), "es"));

        if (cancelled || requestId !== catalogModalDescriptionsSeq.current) return;

        setCatalogModalDescs(normalized);
        setDescCache((prev) => ({
          ...prev,
          [descCacheKey(currentTenderId, catalogModalProduct)]: normalized,
        }));
      } catch {
        if (!cancelled && requestId === catalogModalDescriptionsSeq.current) {
          setCatalogModalDescs([]);
        }
      }
    }

    loadCatalogModalDescriptions();

    return () => {
      cancelled = true;
    };
  }, [showCatalogModal, catalogModalProduct, manualItemsMode, currentTenderId]);

  return {
    products,
    units,
    descOptions,
    setDescOptions,
    descCache,
    setDescCache,
    loadingCatalogs,
    catalogModalDescs,
    setCatalogModalDescs,
  };
}
